import {
  type QueryKey,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

type OrvalFnNoParams<T> = (options?: RequestInit) => Promise<T>;

type OrvalFnWithParams<P extends object, T> = (
  params: P,
  options?: RequestInit
) => Promise<T>;

type ApiQueryOptionsBase<T> = Omit<
  UseQueryOptions<T, Error, T, QueryKey>,
  'queryKey' | 'queryFn'
>;

export type UseApiQueryNoParamsOptions<T> = ApiQueryOptionsBase<T> & {
  queryKey: QueryKey;
  apiFn: OrvalFnNoParams<T>;
};

export type UseApiQueryOptions<
  P extends object,
  T,
  S = undefined,
> = ApiQueryOptionsBase<T> & {
  queryKey: QueryKey;
  apiFn: S extends undefined
    ? OrvalFnWithParams<P, T>
    : OrvalFnWithParamsAndSearch<P, S, T>;
  params: P;
  searchParams?: S;
};

type OrvalFnWithParamsAndSearch<P extends object, S, T> = (
  params: P,
  searchParams: S,
  options?: RequestInit
) => Promise<T>;

/**
 * React Query hook wired to a zero-arg Orval-generated API function.
 * Automatically forwards AbortSignal for request cancellation.
 */
export function useApiQueryNoParams<T>(
  options: UseApiQueryNoParamsOptions<T>
): UseQueryResult<T, Error> {
  const { queryKey, apiFn, ...queryOptions } = options;

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => apiFn({ signal }),
    ...queryOptions,
  });
}

/**
 * React Query hook wired to a named-parameter Orval-generated API function.
 * Automatically forwards AbortSignal for request cancellation.
 */
export function useApiQuery<P extends object, T, S = undefined>(
  options: UseApiQueryOptions<P, T, S>
): UseQueryResult<T, Error> {
  const { queryKey, apiFn, params, searchParams, ...queryOptions } = options;

  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      searchParams !== undefined
        ? (apiFn as OrvalFnWithParamsAndSearch<P, S, T>)(params, searchParams, {
            signal,
          })
        : (apiFn as OrvalFnWithParams<P, T>)(params, { signal }),
    ...queryOptions,
  });
}
